import { WorkflowTaskFormValues } from './types';
import { schema } from '@forms/helpers';
import * as z from 'zod';
import { options } from '@components/ui/form/picker/constants';
import { Appearance } from '@root/globalTypes';

export const formSchema = z
  .object({
    name: schema.input({ name: 'name', required: true }),
    taskType: schema.input({ name: 'type', required: true }),
    important: schema.boolean(),
    dateConfirmation: schema.input(),
    foremanSpecialty: schema.input(),
    scheduleType: schema.input({ name: 'scheduleType', required: true }),
    taskConfirmation: schema.input({ name: 'taskConfirmation', required: true }),
    count: schema.input(),
    unit: schema.input(),
    state: schema.input(),
    importantSchedule: schema.boolean(),
    concurrentSchedules: schema.boolean(),
    nonObligatory: schema.input({ name: 'nonObligatory', required: true }),
    materialId: schema.input(),
    scheduleDependentTaskId: schema.input(),
    confirmationDependentSubtaskId: schema.input(),
    readyToSchedule: z
      .object({
        enabled: schema.boolean(),
        rules: z.array(
          z.object({
            optionId: schema.input(),
            taskName: schema.input(),
            stageName: schema.input(),
            status: schema.input(),
            unitIdentityId: schema.input(),
          }),
        ),
      })
      .superRefine(({ enabled, rules }, ctx) => {
        if (enabled) {
          rules.forEach((rule, i) => {
            if (!rule.optionId.length) {
              ctx.addIssue({
                code: 'too_small',
                minimum: 1,
                inclusive: false,
                type: 'string',
                path: [`rules[${i}].optionId`],
                message: 'default.select.required',
              });
            }
            if (!rule.status.length) {
              ctx.addIssue({
                code: 'too_small',
                minimum: 1,
                inclusive: false,
                type: 'string',
                path: [`rules[${i}].status`],
                message: 'default.select.required',
              });
            }
          });
        }
      }),
    manuallyAdded: schema.input({ name: 'manuallyAdded', required: true }),
    multiplicable: schema.boolean(),
    routePlannerPriority: schema.boolean(),
    releasesInventory: schema.boolean(),
    billing: schema.boolean(),
    appearance: z.object({
      type: schema.input(),
      color: schema.select({ name: 'appearance.color', options }),
      text: schema.input(),
      icon: schema.select({ name: 'appearance.icon', options }),
      isPrefix: schema.boolean(),
      prefix: z.object({
        color: schema.select({ name: 'appearance.prefix.color', options }),
        icon: schema.select({ name: 'appearance.prefix.icon', options }),
        text: schema.input(),
      }),
    }),
  })
  .superRefine(
    (
      {
        scheduleType,
        taskConfirmation,
        taskType,
        materialId,
        dateConfirmation,
        foremanSpecialty,
        count,
        appearance,
        importantSchedule,
        confirmationDependentSubtaskId,
        important,
      },
      ctx,
    ) => {
      const { type, text, color, icon, isPrefix, prefix } = appearance;
      if (taskType === 'material_order' || taskType === 'material_measurement') {
        if (!materialId) {
          ctx.addIssue({
            code: 'too_small',
            minimum: 1,
            inclusive: false,
            type: 'string',
            path: ['materialId'],
            message: 'material.required',
          });
        }
      }
      if (scheduleType === 'internal') {
        if (!foremanSpecialty) {
          ctx.addIssue({
            code: 'too_small',
            minimum: 1,
            inclusive: false,
            type: 'string',
            path: ['foremanSpecialty'],
            message: 'foremanSpecialty.required',
          });
        }
        if (!color) {
          ctx.addIssue({
            code: 'too_small',
            minimum: 1,
            inclusive: false,
            type: 'string',
            path: ['appearance.color'],
            message: 'appearance.color.required',
          });
        }
        if (important) {
          if (!type) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: ['appearance.type'],
              message: 'appearance.type.required',
            });
          }
          if (type === 'point' && !icon && !text) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: ['appearance.icon'],
              message: 'appearance.icon.required',
            });
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: ['appearance.text'],
              message: 'appearance.text.required',
            });
          }
        }
        if (isPrefix) {
          if (!prefix.color) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: ['appearance.prefix.color'],
              message: 'appearance.color.required',
            });
          }
          if (!prefix.icon && !prefix.text) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: ['appearance.prefix.icon'],
              message: 'appearance.prefix.icon.required',
            });
          }
        }
      }

      if (['external', 'dependent'].includes(scheduleType)) {
        if (!icon && !text) {
          ctx.addIssue({
            code: 'too_small',
            minimum: 1,
            inclusive: false,
            type: 'string',
            path: ['appearance.icon'],
            message: 'appearance.icon.required',
          });
        }
        if (importantSchedule && !color) {
          if (!color) {
            ctx.addIssue({
              code: 'too_small',
              minimum: 1,
              inclusive: false,
              type: 'string',
              path: ['appearance.color'],
              message: 'appearance.color.required',
            });
          }
        }
      }

      if (scheduleType !== 'dependent' && !dateConfirmation) {
        ctx.addIssue({
          code: 'too_small',
          minimum: 1,
          inclusive: false,
          type: 'string',
          path: ['dateConfirmation'],
          message: 'dateConfirmation.required',
        });
      }

      if (scheduleType === 'dependent' && !count.length) {
        ctx.addIssue({
          code: 'too_small',
          minimum: 1,
          inclusive: false,
          type: 'string',
          path: ['count'],
          message: 'relativeScheduleOffset.required',
        });
      }

      if (taskConfirmation === 'dependent' && !confirmationDependentSubtaskId) {
        ctx.addIssue({
          code: 'too_small',
          minimum: 1,
          inclusive: false,
          type: 'string',
          path: ['confirmationDependentSubtaskId'],
          message: 'confirmationDependentSubtaskId.required',
        });
      }
    },
  );

export const defaultAppearance: Appearance = {
  type: '',
  color: '',
  icon: '',
  text: '',
  isPrefix: false,
  prefix: {
    color: '',
    icon: '',
    text: '',
  },
};

export const defaultValues: WorkflowTaskFormValues = {
  name: '',
  taskType: '',
  important: false,
  dateConfirmation: '',
  foremanSpecialty: '',
  scheduleType: '',
  count: '',
  unit: '',
  state: '',
  materialId: '',
  taskConfirmation: '',
  importantSchedule: false,
  concurrentSchedules: false,
  nonObligatory: '',
  scheduleDependentTaskId: '',
  confirmationDependentSubtaskId: '',
  readyToSchedule: {
    enabled: false,
    rules: [{ optionId: '', stageName: '', taskName: '', status: '', unitIdentityId: '' }],
  },
  manuallyAdded: '',
  multiplicable: false,
  routePlannerPriority: false,
  releasesInventory: false,
  billing: false,
  appearance: defaultAppearance,
};
